/**
 * Cadrage de la photo source (Lot P3 — CDC §4.6-e, étape 1 du tunnel).
 *
 * Le client cadre au doigt (pan + zoom) dans une fenêtre de ratio choisi ;
 * on extrait la zone visible en RGBA, plafonnée à `maxSide` px sur le plus
 * grand côté (inutile d'échantillonner une photo 4000 px pour une grille de
 * 116 perles — 600 px suffisent largement au bilinéaire).
 */

export type CropAspect = { w: number; h: number };

export type CropState = {
  /** Zoom ≥ 1 (1 = l'image couvre exactement la fenêtre de cadrage). */
  zoom: number;
  /** Décalage du centre, en fraction de la zone débordante (-1..1). */
  offsetX: number;
  offsetY: number;
};

export const DEFAULT_CROP: CropState = { zoom: 1, offsetX: 0, offsetY: 0 };

/** Rectangle source (px image) correspondant à l'état de cadrage. */
export function cropRect(
  imgWidth: number,
  imgHeight: number,
  aspect: CropAspect,
  state: CropState,
): { x: number; y: number; width: number; height: number } {
  const targetRatio = aspect.w / aspect.h;
  const imgRatio = imgWidth / imgHeight;
  const zoom = Math.max(1, state.zoom);

  // Fenêtre "cover" : la plus grande zone de l'image au ratio cible.
  let baseW: number;
  let baseH: number;
  if (imgRatio > targetRatio) {
    baseH = imgHeight;
    baseW = imgHeight * targetRatio;
  } else {
    baseW = imgWidth;
    baseH = imgWidth / targetRatio;
  }

  const width = baseW / zoom;
  const height = baseH / zoom;
  const slackX = (imgWidth - width) / 2;
  const slackY = (imgHeight - height) / 2;
  const x = clamp(slackX + clampUnit(state.offsetX) * slackX, 0, imgWidth - width);
  const y = clamp(slackY + clampUnit(state.offsetY) * slackY, 0, imgHeight - height);
  return { x, y, width, height };
}

export type CroppedPixels = {
  rgba: Uint8ClampedArray;
  width: number;
  height: number;
};

/**
 * Extrait la zone cadrée en RGBA (plafonnée à `maxSide`). Nécessite le DOM
 * (canvas) — appelé depuis l'UI, jamais depuis le worker.
 */
export function extractCrop(
  img: HTMLImageElement,
  aspect: CropAspect,
  state: CropState,
  maxSide = 600,
): CroppedPixels {
  const rect = cropRect(img.naturalWidth, img.naturalHeight, aspect, state);
  const scale = Math.min(1, maxSide / Math.max(rect.width, rect.height));
  const outW = Math.max(1, Math.round(rect.width * scale));
  const outH = Math.max(1, Math.round(rect.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D indisponible.');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height, 0, 0, outW, outH);
  const data = ctx.getImageData(0, 0, outW, outH);
  return { rgba: data.data, width: outW, height: outH };
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function clampUnit(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(-1, Math.min(1, v));
}
